import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Badge, Spinner, Alert, Form, Modal, Accordion } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faEye, faChartLine, faCheckCircle, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { useSchool } from '../../contexts/SchoolContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import SchoolUserService from '../../services/schoolUser.service';
import LessonPlanService from '../../services/lessonPlan.service';

const TeacherLessonPlans = () => {
  const { selectedSchool } = useSchool();
  const { selectedYear } = useAcademicYear();
  const [lessonPlans, setLessonPlans] = useState([]);
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    subject: '',
    grade: ''
  });
  const [showViewModal, setShowViewModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);

  useEffect(() => {
    if (selectedSchool && selectedYear) {
      fetchTeacherSubjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchool, selectedYear]);

  useEffect(() => {
    if (teacherSubjects.length > 0) {
      fetchLessonPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherSubjects, filters]);

  const fetchTeacherSubjects = async () => {
    try {
      setLoading(true);
      // Backend filtra automaticamente baseado no papel do usuário
      const response = await SchoolUserService.getTeacherAssignments({
        school: selectedSchool,
        year: selectedYear
      });
      setTeacherSubjects(response.results || []);
    } catch (err) {
      console.error('Error fetching teacher subjects:', err);
      setError('Erro ao carregar disciplinas.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        year: selectedYear
      };

      // Usar subject_name e grade_name para filtrar por nome em vez de UUID
      if (filters.subject) params.subject_name = filters.subject;
      if (filters.grade) params.grade_name = filters.grade;

      const response = await LessonPlanService.getAll(params);
      setLessonPlans(response.results || []);
    } catch (err) {
      console.error('Error fetching lesson plans:', err);
      setError('Erro ao carregar planos de aula.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonPlanProgress = async (planId) => {
    try {
      setLoadingProgress(true);
      const response = await LessonPlanService.getProgress(planId);
      setProgressData(response);
    } catch (err) {
      console.error('Error fetching lesson plan progress:', err);
      setProgressData(null);
    } finally {
      setLoadingProgress(false);
    }
  };

  const handleView = (plan) => {
    setSelectedPlan(plan);
    setShowViewModal(true);
  };

  const handleViewProgress = async (plan) => {
    setSelectedPlan(plan);
    setShowProgressModal(true);
    setProgressData(null);

    if (plan.id) {
      await fetchLessonPlanProgress(plan.id);
    }
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedPlan(null);
  };

  const handleCloseProgressModal = () => {
    setShowProgressModal(false);
    setSelectedPlan(null);
    setProgressData(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      subject: '',
      grade: ''
    });
  };

  const getUniqueSubjects = () => {
    const subjects = new Map();
    teacherSubjects.forEach(ts => {
      // Usar subject_name como chave para agrupar disciplinas únicas
      if (ts.subject_name && !subjects.has(ts.subject_name)) {
        subjects.set(ts.subject_name, ts.subject_name);
      }
    });
    // Retornar array com name como id e name (para compatibilidade com filtro)
    return Array.from(subjects.keys()).map(name => ({ id: name, name }));
  };

  const getUniqueGrades = () => {
    const grades = new Map();
    teacherSubjects.forEach(ts => {
      // Criar chave única combinando série + nível de ensino
      const key = `${ts.classroom_grade} - ${ts.classroom_education_level}`;
      if (!grades.has(key)) {
        grades.set(key, {
          id: key,
          name: key,
          grade: ts.classroom_grade,
          education_level: ts.classroom_education_level
        });
      }
    });
    return Array.from(grades.values());
  };

  const getGradeName = (plan) => {
    return `${plan.grade_name} - ${plan.grade_education_level}` || '-';
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Planos de Aula - {selectedYear}</h4>
          <p className="mb-0">Visualize os planos de aula das suas disciplinas</p>
        </div>
      </div>

      {/* Filtros */}
      <Row className="mb-4">
        <Col xs={12}>
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Disciplina</Form.Label>
                    <Form.Select
                      name="subject"
                      value={filters.subject}
                      onChange={handleFilterChange}
                    >
                      <option value="">Todas</option>
                      {getUniqueSubjects().map(subject => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Série</Form.Label>
                    <Form.Select
                      name="grade"
                      value={filters.grade}
                      onChange={handleFilterChange}
                    >
                      <option value="">Todas</option>
                      {getUniqueGrades().map(grade => (
                        <option key={grade.id} value={grade.id}>
                          {grade.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button
                    variant="secondary"
                    onClick={handleClearFilters}
                    className="w-100"
                  >
                    Limpar Filtros
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lista de Planos de Aula */}
      <Row>
        <Col xs={12}>
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faBook} className="me-2" />
                    Planos de Aula
                  </h5>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              {loading && (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </Spinner>
                </div>
              )}
              {error && (
                <Alert variant="danger" className="m-3">
                  {error}
                </Alert>
              )}
              {!loading && !error && lessonPlans.length === 0 && (
                <div className="text-center py-5">
                  <p className="text-muted">Nenhum plano de aula encontrado.</p>
                </div>
              )}
              {!loading && !error && lessonPlans.length > 0 && (
                <Table responsive hover className="align-items-center table-flush mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>Disciplina</th>
                      <th>Série</th>
                      <th>Carga Horária</th>
                      <th>Competências</th>
                      <th>Habilidades</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lessonPlans.map((plan, index) => (
                      <tr key={index}>
                        <td className="fw-bold">
                            {plan.subject_name || '-'}
                        </td>
                        <td className="fw-bold">
                          {getGradeName(plan)}
                        </td>
                        <td>
                          {plan.workload || 0} aula(s)
                        </td>
                        <td>
                          <Badge bg="secondary">
                            {plan.competences?.length || 0}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg="secondary">
                            {plan.skills?.length || 0}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <Button
                            variant="info"
                            size="sm"
                            onClick={() => handleView(plan)}
                            className="me-2"
                            title="Ver Competências e Habilidades"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleViewProgress(plan)}
                            title="Ver Progresso"
                          >
                            <FontAwesomeIcon icon={faChartLine} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de Visualização - Competências e Habilidades */}
      <Modal show={showViewModal} onHide={handleCloseViewModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faEye} className="me-2" />
            Competências e Habilidades
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedPlan && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Disciplina:</strong> {selectedPlan.subject_name}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Série:</strong> {selectedPlan.grade_name}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Ano:</strong> {selectedPlan.year}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Carga Horária:</strong> {selectedPlan.workload || 0} aula(s)</p>
                </Col>
              </Row>

              {selectedPlan.competences_details && selectedPlan.competences_details.length > 0 ? (
                <Accordion defaultActiveKey="0">
                  {selectedPlan.competences_details.map((competence, idx) => {
                    const competenceSkills = selectedPlan.skills_details?.filter(
                      skill => skill.competence === competence.id
                    ) || [];

                    return (
                      <Accordion.Item eventKey={String(idx)} key={idx}>
                        <Accordion.Header>
                          <strong>{competence.code}</strong>
                          <span className="ms-2 text-muted">
                            ({competenceSkills.length} habilidade{competenceSkills.length !== 1 ? 's' : ''})
                          </span>
                        </Accordion.Header>
                        <Accordion.Body>
                          {competence.description && (
                            <p className="mb-3"><em>{competence.description}</em></p>
                          )}

                          {competenceSkills.length > 0 && (
                            <>
                              <h6 className="mb-2">Habilidades:</h6>
                              <ul>
                                {competenceSkills.map((skill, skillIdx) => (
                                  <li key={skillIdx}>
                                    <strong>{skill.code}</strong>
                                    {skill.description && ` - ${skill.description}`}
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              ) : (
                <Alert variant="info">
                  Nenhuma competência cadastrada para este plano de aula.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseViewModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Progresso */}
      <Modal show={showProgressModal} onHide={handleCloseProgressModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faChartLine} className="me-2" />
            Progresso do Plano de Aula
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedPlan && (
            <>
              <Card className="mb-3">
                <Card.Body>
                  <h6 className="text-muted mb-3">Informações do Plano</h6>
                  <Row>
                    <Col md={6}>
                      <p><strong>Disciplina:</strong> {selectedPlan.subject_name}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Série:</strong> {selectedPlan.grade_name}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Ano:</strong> {selectedPlan.year}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Carga Horária:</strong> {selectedPlan.workload || 0} aula(s)</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {loadingProgress && (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Carregando estatísticas...</span>
                </div>
              )}

              {!loadingProgress && progressData && (
                <>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Card className="border-left-primary shadow h-100 py-2">
                        <Card.Body>
                          <Row className="align-items-center">
                            <Col>
                              <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                Aulas Ministradas
                              </div>
                              <div className="h5 mb-0 font-weight-bold text-gray-800">
                                {progressData.statistics.workload.total_taught_classes} / {progressData.statistics.workload.total_planned_classes}
                              </div>
                              <div className="mt-2">
                                <Badge bg={progressData.statistics.workload.completion_percentage >= 75 ? 'success' : progressData.statistics.workload.completion_percentage >= 50 ? 'warning' : 'danger'}>
                                  {progressData.statistics.workload.completion_percentage}%
                                </Badge>
                              </div>
                            </Col>
                            <Col xs="auto">
                              <FontAwesomeIcon icon={faCalendarAlt} size="2x" className="text-gray-300" />
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={4}>
                      <Card className="border-left-success shadow h-100 py-2">
                        <Card.Body>
                          <Row className="align-items-center">
                            <Col>
                              <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                                Competências
                              </div>
                              <div className="h5 mb-0 font-weight-bold text-gray-800">
                                {progressData.statistics.competences.total_taught} / {progressData.statistics.competences.total_planned}
                              </div>
                              <div className="mt-2">
                                <Badge bg={progressData.statistics.competences.completion_percentage >= 75 ? 'success' : progressData.statistics.competences.completion_percentage >= 50 ? 'warning' : 'danger'}>
                                  {progressData.statistics.competences.completion_percentage}%
                                </Badge>
                              </div>
                            </Col>
                            <Col xs="auto">
                              <FontAwesomeIcon icon={faCheckCircle} size="2x" className="text-gray-300" />
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={4}>
                      <Card className="border-left-info shadow h-100 py-2">
                        <Card.Body>
                          <Row className="align-items-center">
                            <Col>
                              <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                                Habilidades
                              </div>
                              <div className="h5 mb-0 font-weight-bold text-gray-800">
                                {progressData.statistics.skills.total_taught} / {progressData.statistics.skills.total_planned}
                              </div>
                              <div className="mt-2">
                                <Badge bg={progressData.statistics.skills.completion_percentage >= 75 ? 'success' : progressData.statistics.skills.completion_percentage >= 50 ? 'warning' : 'danger'}>
                                  {progressData.statistics.skills.completion_percentage}%
                                </Badge>
                              </div>
                            </Col>
                            <Col xs="auto">
                              <FontAwesomeIcon icon={faCheckCircle} size="2x" className="text-gray-300" />
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {progressData.taught_classes && progressData.taught_classes.length > 0 && (
                    <Card className="mb-3">
                      <Card.Header>
                        <h6 className="mb-0">
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                          Aulas Ministradas ({progressData.taught_classes.length})
                        </h6>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <Table responsive hover className="mb-0">
                          <thead className="thead-light">
                            <tr>
                              <th>Data</th>
                              <th>Turma</th>
                              <th>Professor</th>
                              <th>Competências</th>
                              <th>Habilidades</th>
                            </tr>
                          </thead>
                          <tbody>
                            {progressData.taught_classes.map((classItem, idx) => (
                              <tr key={idx}>
                                <td>{new Date(classItem.date).toLocaleDateString('pt-BR')}</td>
                                <td>{classItem.classroom}</td>
                                <td>{classItem.teacher}</td>
                                <td>
                                  <Badge bg="secondary">{classItem.competences_count}</Badge>
                                </td>
                                <td>
                                  <Badge bg="secondary">{classItem.skills_count}</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}

                  {progressData.taught_classes && progressData.taught_classes.length === 0 && (
                    <Alert variant="info">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                      Nenhuma aula foi ministrada ainda para este plano.
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseProgressModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TeacherLessonPlans;
